import { CommonModule } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, RouterLink } from '@angular/router'
import { AdminService, ApiKeyAdmin, ApiKeyDeliveryStatus } from '@services/admin.service'
import { AuthenticationService, AuthUser } from '@services/authentication.service'
import { switchMap, take } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

@Component({
  imports: [CommonModule, RouterLink],
  selector: 'app-api-key-reveal',
  standalone: true,
  styleUrls: ['./api-key-reveal.component.css'],
  templateUrl: './api-key-reveal.component.html',
})
export class ApiKeyRevealComponent implements OnInit {
    apiKey: ApiKeyAdmin | null = null
    apiKeySecret: string | null = null
    copied = false
    copyError = ''
    deliveryStatus: ApiKeyDeliveryStatus | null = null
    errorMessage = ''
    loadingMessage = 'Checking delivery link...'
    needsSignIn = false
    keyId = ''
    state: 'loading' | 'ready' | 'revealing' | 'revealed' | 'error' = 'loading'
    token = ''
    readonly currentUser$ = this.authService.user$

    constructor(
        private adminService: AdminService,
        private authService: AuthenticationService,
        private route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this.keyId = this.route.snapshot.queryParamMap.get('key_id') ?? ''
        this.token = this.route.snapshot.queryParamMap.get('token') ?? ''

        if (!this.keyId || !this.token) {
            this.state = 'error'
            this.errorMessage = 'This API key delivery link is missing required details.'
            return
        }

        this.loadDeliveryStatus()
    }

    loadDeliveryStatus(): void {
        this.state = 'loading'
        this.loadingMessage = 'Checking delivery link...'
        this.errorMessage = ''
        this.needsSignIn = false
        this.deliveryStatus = null

        this.authService.getAuthenticatedToken().pipe(
            take(1),
            switchMap(() => this.adminService.getApiKeyDeliveryStatus(this.keyId, this.token))
        ).subscribe({
            next: status => {
                this.deliveryStatus = status
                this.apiKey = status.apiKey ?? null
                this.state = 'ready'
            },
            error: (error: HttpErrorResponse) => {
                if (this.redirectForRecoverableAuthError(error)) return

                const status = error.error as ApiKeyDeliveryStatus | undefined
                this.deliveryStatus = status ?? null
                this.apiKey = status?.apiKey ?? null
                this.state = 'error'
                this.needsSignIn = this.isAuthError(error)
                this.errorMessage = this.getDeliveryErrorMessage(error)
            },
        })
    }

    reveal(): void {
        if (!this.keyId || !this.token || this.state === 'revealing') return

        this.state = 'revealing'
        this.needsSignIn = false
        this.authService.getAuthenticatedToken().pipe(
            take(1),
            switchMap(() => this.adminService.revealApiKey(this.keyId, this.token))
        ).subscribe({
            next: revealed => {
                this.apiKey = revealed.apiKey
                this.apiKeySecret = revealed.key
                this.state = 'revealed'
            },
            error: (error: HttpErrorResponse) => {
                if (this.redirectForRecoverableAuthError(error)) return

                this.state = 'error'
                this.needsSignIn = this.isAuthError(error)
                this.errorMessage = this.getDeliveryErrorMessage(error)
            },
        })
    }

    get browserTestUrl(): string {
        const path = this.getBrowserTestPath()
        return this.apiKeySecret
            ? `${environment.api}${path}?api_key=${encodeURIComponent(this.apiKeySecret)}`
            : `${environment.api}${path}?api_key=YOUR_API_KEY`
    }

    copy(value: string): void {
        this.copyError = ''
        this.writeToClipboard(value).then(() => {
            this.copied = true
            setTimeout(() => this.copied = false, 2000)
        }).catch(() => {
            this.copyError = 'Failed to copy to clipboard.'
        })
    }

    getInitials(user: AuthUser | null | undefined): string {
        const source = user?.name || user?.email || 'K'
        return source
            .split(/\s+|@/)
            .filter(Boolean)
            .slice(0, 2)
            .map(part => part[0]?.toUpperCase())
            .join('') || 'K'
    }

    getRecipientLabel(): string {
        return this.apiKey?.recipient_name || this.apiKey?.recipient_email || this.apiKey?.recipient_user_id || 'Assigned recipient'
    }

    getRecipientSubtext(): string {
        return this.apiKey?.recipient_email || this.apiKey?.recipient_user_id || ''
    }

    getApiKeyScopesLabel(): string {
        const scopes = this.apiKey?.scopes ?? []
        return scopes.length > 0 ? scopes.join(', ') : 'No scopes listed'
    }

    getStatusSummary(): string {
        if (!this.deliveryStatus) return 'Checking delivery link'
        if (this.deliveryStatus.code === 'ready') return 'Ready to reveal'
        return this.deliveryStatus.message
    }

    signInAgain(): void {
        this.startAuthRedirect(true)
    }

    private isAuthError(error: any): boolean {
        const message = [
            error?.error,
            error?.error?.message,
            error?.message,
            error?.cause?.error,
            error?.cause?.message,
        ].filter(Boolean).join(' ').toLowerCase()
        return error?.status === 401
            || message.includes('login_required')
            || message.includes('consent_required')
            || message.includes('interaction_required')
            || message.includes('missing_refresh_token')
            || message.includes('missing refresh token')
    }

    private getDeliveryErrorMessage(error: HttpErrorResponse): string {
        const body = error.error as ApiKeyDeliveryStatus | undefined

        if (this.isAuthError(error)) return 'Your Kartorium sign-in expired. Sign in again, then return to this reveal link.'
        if (body?.message) return body.message
        if (error.status === 401) return 'Sign in again before revealing this API key.'
        if (error.status === 403) return 'This delivery link is assigned to a different signed-in user or organization.'
        if (error.status === 409) return 'This API key has already been revealed.'
        if (error.status === 410) return 'This API key delivery link has expired or was revoked.'

        return 'This API key delivery link is invalid.'
    }

    private redirectForRecoverableAuthError(error: HttpErrorResponse): boolean {
        if (!this.isAuthError(error)) return false

        const retryKey = this.getAuthRetryStorageKey()
        if (sessionStorage.getItem(retryKey)) return false

        sessionStorage.setItem(retryKey, '1')
        this.state = 'loading'
        this.loadingMessage = 'Redirecting to sign in...'
        this.startAuthRedirect(false)

        return true
    }

    private startAuthRedirect(force = false): void {
        const retryKey = this.getAuthRetryStorageKey()
        if (force) sessionStorage.setItem(retryKey, '1')

        const organization = this.authService.currentUser?.org_id
        const options: any = {
            appState: { target: this.getRevealTarget() },
        }
        if (organization) {
            options.authorizationParams = { organization }
        }

        this.authService.loginWithRedirect(options)
    }

    private getRevealTarget(): string {
        const params = new URLSearchParams({
            key_id: this.keyId,
            token: this.token,
        })

        return `/api-key/reveal?${params.toString()}`
    }

    private getBrowserTestPath(): string {
        const scopes = new Set(this.apiKey?.scopes ?? [])
        if (scopes.has('projects:read')) return '/client/projects'
        if (scopes.has('organization:read')) return '/client/organization'
        if (scopes.has('oee:read')) return '/client/oee/sites'
        if (scopes.has('assets:read')) return '/client/asset-types'
        if (scopes.has('models:read')) return '/client/models'
        if (scopes.has('files:read')) return '/client/files'
        if (scopes.has('search:read')) return '/client/search/assets'
        return '/client/projects'
    }

    private getAuthRetryStorageKey(): string {
        return `apiKeyRevealAuthRetry:${this.keyId}`
    }

    private async writeToClipboard(value: string): Promise<void> {
        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(value)
                return
            } catch {
                // Embedded browsers can expose navigator.clipboard but reject writes.
            }
        }

        const textarea = document.createElement('textarea')
        textarea.value = value
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        textarea.style.top = '0'
        document.body.appendChild(textarea)
        textarea.select()

        try {
            const copied = document.execCommand('copy')
            if (!copied) throw new Error('copy command failed')
        } finally {
            document.body.removeChild(textarea)
        }
    }
}
